import { useMemo, useState } from 'react';
import {
  Autocomplete, TextField, InputAdornment, Box, Stack, Typography, Chip, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useGetSearchMerchantQuery } from '../../../../redux/commonApiCalls';

// Search for an existing merchant and load them straight into the Pricing Setup
// wizard — saves jumping back to the Onboarding Queue. Mirrors the listing's
// search (client-side filter over a single fetch of non-test merchants).
const SEARCH_FILTER = {
  andfilter: [{ field: 'isTestMerchant', operator: 'ne', value: true }],
  offset: 0,
  limit: 500,
  includes: 'id,name,companyName,merchantId,email,status',
};

export default function MerchantSearchBar({ onSelect }) {
  const [input, setInput] = useState('');
  const { data: apiData, isFetching } = useGetSearchMerchantQuery(SEARCH_FILTER);

  const options = useMemo(() => {
    const list = Array.isArray(apiData) ? apiData : Array.isArray(apiData?.data) ? apiData.data : [];
    return list.map((m) => ({
      id: m.id || m._id,
      merchantId: m.merchantId,
      name: m.name || '',
      companyName: m.companyName || m.name || '',
      email: m.email || '',
      status: m.status || '',
    }));
  }, [apiData]);

  return (
    <Box
      sx={{
        border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper',
        p: 1.75, mb: 1.75,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Resume an existing merchant</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Search by name, company, email or merchant ID to load their pricing setup.
          </Typography>
        </Box>
        <Autocomplete
          sx={{ flex: 1, width: '100%' }}
          size="small"
          loading={isFetching}
          options={options}
          inputValue={input}
          onInputChange={(_, v) => setInput(v)}
          getOptionLabel={(o) => o.companyName || o.name || o.id || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return opts.slice(0, 50);
            return opts
              .filter((o) =>
                o.name.toLowerCase().includes(q) ||
                o.companyName.toLowerCase().includes(q) ||
                o.email.toLowerCase().includes(q) ||
                String(o.id).toLowerCase().includes(q) ||
                String(o.merchantId || '').toLowerCase().includes(q)
              )
              .slice(0, 50);
          }}
          onChange={(_, value) => { if (value?.id) onSelect(value.id); }}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {o.companyName || o.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                  {o.email || o.id}{o.status ? ` · ${o.status}` : ''}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search merchants…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {isFetching ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: 'grey.50' } }}
            />
          )}
        />
        <Chip size="small" label="optional" variant="outlined" sx={{ flexShrink: 0, fontSize: 10.5 }} />
      </Stack>
    </Box>
  );
}
